import lowerCase from 'lodash/lowerCase'

// What a country calls its subdivisions. Drives the picker label and nothing else.
export type SubdivisionType = 'canton' | 'community' | 'county' | 'department' | 'entity' | 'island' | 'nation' | 'parish' | 'province' | 'region' | 'state'

export interface ISubdivision {
  name: string
  // Full ISO 3166-2, e.g. 'DE-BY'. Use bareSubdivisionCode() for the bare suffix the holiday
  // library expects.
  iso: string
}

export interface ICountry {
  name: string
  iso: string
  googleId?: string
  subdivisions?: ISubdivision[]
  subdivisionType?: SubdivisionType
  searchAliases?: string[]
  // Retired by ISO 3166-3. Kept so already-stored codes still resolve to a name; never selectable.
  deprecated?: true
  // Codes that replaced this one when it split or merged.
  supersededBy?: string[]
}

// A location's country plus, where the country has them, one subdivision.
export interface IPlace {
  countryCode: string
  subdivisionCode: string | null
}

export interface ISubdivisionOption {
  value: string
  label: string
}

// https://www.googleapis.com/calendar/v3/calendars/en.${values.country}%23holiday%40group.v.calendar.google.com/events?key=AIzaSyDKEPIrmlCfBp9OpeW-vg6eQg1eWTarcZo&maxResults=2500

//google country code to iso code:
//https://gist.github.com/seanblanchfield/4df78d73c85b149133615c7953894076

export const countries: ICountry[] = [
  { iso: 'AD', name: 'Andorra' },
  { iso: 'AE', name: 'United Arab Emirates' },
  { iso: 'AF', name: 'Afghanistan', googleId: 'af' },
  { iso: 'AG', name: 'Antigua & Barbuda' },
  { iso: 'AI', name: 'Anguilla' },
  { iso: 'AL', name: 'Albania' },
  { iso: 'AM', name: 'Armenia' },
  { iso: 'AO', name: 'Angola' },
  { iso: 'AQ', name: 'Antarctica', googleId: 'aq' },
  { iso: 'AR', name: 'Argentina' },
  { iso: 'AS', name: 'American Samoa' },
  { iso: 'AT', name: 'Austria' },
  {
    iso: 'AU',
    name: 'Australia',
    subdivisionType: 'state',
    subdivisions: [
      { iso: 'AU-ACT', name: 'Australian Capital Territory' },
      { iso: 'AU-NSW', name: 'New South Wales' },
      { iso: 'AU-NT', name: 'Northern Territory' },
      { iso: 'AU-QLD', name: 'Queensland' },
      { iso: 'AU-SA', name: 'South Australia' },
      { iso: 'AU-TAS', name: 'Tasmania' },
      { iso: 'AU-VIC', name: 'Victoria' },
      { iso: 'AU-WA', name: 'Western Australia' },
    ],
  },
  { iso: 'AW', name: 'Aruba' },
  { iso: 'AX', name: 'Åland Islands' },
  { iso: 'AZ', name: 'Azerbaijan' },
  { iso: 'BA', name: 'Bosnia and Herzegovina' },
  { iso: 'BB', name: 'Barbados' },
  { iso: 'BD', name: "People's Republic of Bangladesh" },
  { iso: 'BE', name: 'Belgium' },
  { iso: 'BF', name: 'Burkina Faso' },
  { iso: 'BG', name: 'Bulgaria' },
  { iso: 'BH', name: 'Bahrain' },
  { iso: 'BI', name: 'Burundi' },
  { iso: 'BJ', name: 'Benin' },
  { iso: 'BL', name: 'St. Barths' },
  { iso: 'BM', name: 'Bermuda' },
  { iso: 'BN', name: 'Brunei' },
  { iso: 'BO', name: 'Bolivia' },
  { iso: 'BQ', name: 'Caribbean Netherlands' },
  { iso: 'BR', name: 'Brazil' },
  { iso: 'BS', name: 'Bahamas' },
  { iso: 'BT', name: 'Bhutan', googleId: 'bt' },
  { iso: 'BV', name: 'Bouvet Island', googleId: 'bv' },
  { iso: 'BW', name: 'Botswana' },
  { iso: 'BY', name: 'Belarus' },
  { iso: 'BZ', name: 'Belize' },
  {
    iso: 'CA',
    name: 'Canada',
    googleId: 'canadian',
    subdivisionType: 'province',
    subdivisions: [
      { iso: 'CA-AB', name: 'Alberta' },
      { iso: 'CA-BC', name: 'British Columbia' },
      { iso: 'CA-MB', name: 'Manitoba' },
      { iso: 'CA-NB', name: 'New Brunswick' },
      { iso: 'CA-NL', name: 'Newfoundland and Labrador' },
      { iso: 'CA-NS', name: 'Nova Scotia' },
      { iso: 'CA-NT', name: 'Northwest Territories' },
      { iso: 'CA-NU', name: 'Nunavut' },
      { iso: 'CA-ON', name: 'Ontario' },
      { iso: 'CA-PE', name: 'Prince Edward Island' },
      { iso: 'CA-QC', name: 'Quebec' },
      { iso: 'CA-SK', name: 'Saskatchewan' },
      { iso: 'CA-YT', name: 'Yukon' },
    ],
  },
  { iso: 'CC', name: 'Cocos (Keeling) Islands' },
  { iso: 'CD', name: 'Democratic Republic of the Congo' },
  { iso: 'CF', name: 'Central African Republic' },
  { iso: 'CG', name: 'Republic of the Congo' },
  {
    iso: 'CH',
    name: 'Switzerland',
    subdivisionType: 'canton',
    subdivisions: [
      { iso: 'CH-AG', name: 'Aargau' },
      { iso: 'CH-AI', name: 'Appenzell Innerrhoden' },
      { iso: 'CH-AR', name: 'Appenzell Ausserrhoden' },
      { iso: 'CH-BE', name: 'Bern' },
      { iso: 'CH-BL', name: 'Basel-Landschaft' },
      { iso: 'CH-BS', name: 'Basel-Stadt' },
      { iso: 'CH-FR', name: 'Fribourg' },
      { iso: 'CH-GE', name: 'Genève' },
      { iso: 'CH-GL', name: 'Glarus' },
      { iso: 'CH-GR', name: 'Graubünden' },
      { iso: 'CH-JU', name: 'Jura' },
      { iso: 'CH-LU', name: 'Luzern' },
      { iso: 'CH-NE', name: 'Neuchâtel' },
      { iso: 'CH-NW', name: 'Nidwalden' },
      { iso: 'CH-OW', name: 'Obwalden' },
      { iso: 'CH-SG', name: 'Sankt Gallen' },
      { iso: 'CH-SH', name: 'Schaffhausen' },
      { iso: 'CH-SO', name: 'Solothurn' },
      { iso: 'CH-SZ', name: 'Schwyz' },
      { iso: 'CH-TG', name: 'Thurgau' },
      { iso: 'CH-TI', name: 'Ticino' },
      { iso: 'CH-UR', name: 'Uri' },
      { iso: 'CH-VD', name: 'Vaud' },
      { iso: 'CH-VS', name: 'Valais' },
      { iso: 'CH-ZG', name: 'Zug' },
      { iso: 'CH-ZH', name: 'Zürich' },
    ],
  },
  { iso: 'CI', name: "Republic of Côte d'Ivoire" },
  { iso: 'CK', name: 'Cook Islands' },
  { iso: 'CL', name: 'Chile' },
  { iso: 'CM', name: 'Cameroon' },
  { iso: 'CN', name: 'China' },
  { iso: 'CO', name: 'Colombia' },
  { iso: 'CR', name: 'Costa Rica' },
  { iso: 'CU', name: 'Cuba' },
  { iso: 'CV', name: 'Cape Verde' },
  { iso: 'CW', name: 'Curaçao' },
  { iso: 'CX', name: 'Christmas Island' },
  { iso: 'CY', name: 'Cyprus' },
  { iso: 'CZ', name: 'Czech Republic' },
  {
    iso: 'DE',
    name: 'Germany',
    subdivisionType: 'state',
    subdivisions: [
      { iso: 'DE-BB', name: 'Brandenburg' },
      { iso: 'DE-BE', name: 'Berlin' },
      { iso: 'DE-BW', name: 'Baden-Württemberg' },
      { iso: 'DE-BY', name: 'Bayern' },
      { iso: 'DE-HB', name: 'Bremen' },
      { iso: 'DE-HE', name: 'Hessen' },
      { iso: 'DE-HH', name: 'Hamburg' },
      { iso: 'DE-MV', name: 'Mecklenburg-Vorpommern' },
      { iso: 'DE-NI', name: 'Niedersachsen' },
      { iso: 'DE-NW', name: 'Nordrhein-Westfalen' },
      { iso: 'DE-RP', name: 'Rheinland-Pfalz' },
      { iso: 'DE-SH', name: 'Schleswig-Holstein' },
      { iso: 'DE-SL', name: 'Saarland' },
      { iso: 'DE-SN', name: 'Sachsen' },
      { iso: 'DE-ST', name: 'Sachsen-Anhalt' },
      { iso: 'DE-TH', name: 'Thüringen' },
    ],
  },
  { iso: 'DJ', name: 'Djibouti' },
  { iso: 'DK', name: 'Denmark' },
  { iso: 'DM', name: 'Dominica' },
  { iso: 'DO', name: 'Dominican Republic' },
  { iso: 'DZ', name: 'Algeria' },
  { iso: 'EC', name: 'Ecuador' },
  { iso: 'EE', name: 'Estonia' },
  { iso: 'EG', name: 'Egypt' },
  { iso: 'EH', name: 'Western Sahara' },
  { iso: 'ER', name: 'Eritrea' },
  {
    iso: 'ES',
    name: 'Spain',
    subdivisionType: 'community',
    subdivisions: [
      { iso: 'ES-AN', name: 'Andalucía' },
      { iso: 'ES-AR', name: 'Aragón' },
      { iso: 'ES-AS', name: 'Asturias, Principado de' },
      { iso: 'ES-CB', name: 'Cantabria' },
      { iso: 'ES-CE', name: 'Ceuta' },
      { iso: 'ES-CL', name: 'Castilla y León' },
      { iso: 'ES-CM', name: 'Castilla-La Mancha' },
      { iso: 'ES-CN', name: 'Canarias' },
      { iso: 'ES-CT', name: 'Catalunya' },
      { iso: 'ES-EX', name: 'Extremadura' },
      { iso: 'ES-GA', name: 'Galicia' },
      { iso: 'ES-IB', name: 'Illes Balears' },
      { iso: 'ES-MC', name: 'Murcia, Región de' },
      { iso: 'ES-MD', name: 'Madrid, Comunidad de' },
      { iso: 'ES-ML', name: 'Melilla' },
      { iso: 'ES-NC', name: 'Navarra, Comunidad Foral de' },
      { iso: 'ES-PV', name: 'País Vasco' },
      { iso: 'ES-RI', name: 'La Rioja' },
      { iso: 'ES-VC', name: 'Valenciana, Comunidad' },
    ],
  },
  { iso: 'ET', name: 'Ethiopia' },
  { iso: 'FI', name: 'Finland' },
  { iso: 'FK', name: 'Falkland Islands', googleId: 'fk' },
  { iso: 'FJ', name: 'Fiji' },
  { iso: 'FM', name: 'Micronesia', googleId: 'fm' },
  { iso: 'FO', name: 'Faroe Islands' },
  { iso: 'FR', name: 'France' },
  { iso: 'GA', name: 'Gabon' },
  {
    iso: 'GB',
    name: 'United Kingdom',
    subdivisionType: 'nation',
    subdivisions: [
      { iso: 'GB-ENG', name: 'England' },
      { iso: 'GB-NIR', name: 'Northern Ireland' },
      { iso: 'GB-SCT', name: 'Scotland' },
      { iso: 'GB-WLS', name: 'Wales' },
    ],
  },
  { iso: 'GD', name: 'Grenada' },
  { iso: 'GE', name: 'Georgia' },
  { iso: 'GF', name: 'French Guiana' },
  { iso: 'GG', name: 'Guernsey' },
  { iso: 'GH', name: 'Ghana' },
  { iso: 'GI', name: 'Gibraltar' },
  { iso: 'GL', name: 'Greenland' },
  { iso: 'GM', name: 'The Gambia' },
  { iso: 'GN', name: 'Guinea' },
  { iso: 'GP', name: 'Guadeloupe' },
  { iso: 'GQ', name: 'Republic of Equatorial Guinea' },
  { iso: 'GR', name: 'Greece' },
  { iso: 'GS', name: 'South Georgia & South Sandwich Islands', googleId: 'gs' },
  { iso: 'GT', name: 'Guatemala' },
  { iso: 'GU', name: 'Guam' },
  { iso: 'GW', name: 'Guinea-Bissau' },
  { iso: 'GY', name: 'Guyana' },
  { iso: 'HK', name: 'Hong Kong' },
  { iso: 'HM', name: 'Heard Island and Mcdonald Islands', googleId: 'hm' },
  { iso: 'HN', name: 'Honduras' },
  { iso: 'HR', name: 'Croatia' },
  { iso: 'HT', name: 'Haiti' },
  { iso: 'HU', name: 'Hungary' },
  { iso: 'IC', name: 'Canary Islands' },
  { iso: 'ID', name: 'Indonesia' },
  { iso: 'IE', name: 'Ireland' },
  { iso: 'IL', name: 'Israel' },
  { iso: 'IM', name: 'Isle of Man' },
  { iso: 'IN', name: 'India', googleId: 'indian' },
  { iso: 'IO', name: 'British Indian Ocean Territory', googleId: 'io' },
  { iso: 'IQ', name: 'Iraq', googleId: 'iq' },
  { iso: 'IR', name: 'Iran' },
  { iso: 'IS', name: 'Iceland' },
  { iso: 'IT', name: 'Italia' },
  { iso: 'JE', name: 'Jersey' },
  { iso: 'JM', name: 'Jamaica' },
  { iso: 'JO', name: 'Jordan', googleId: 'jo' },
  { iso: 'JP', name: 'Japan' },
  { iso: 'KE', name: 'Kenya' },
  { iso: 'KG', name: 'Kyrgyzstan', googleId: 'kg' },
  { iso: 'KH', name: 'Cambodia', googleId: 'kh' },
  { iso: 'KI', name: 'Kiribati', googleId: 'ki' },
  { iso: 'KM', name: 'Comoros' },
  { iso: 'KN', name: 'St. Kitts & Nevis' },
  { iso: 'KP', name: 'North Korea', googleId: 'kp' },
  { iso: 'KR', name: 'South Korea' },
  { iso: 'KW', name: 'Kuwait', googleId: 'kw' },
  { iso: 'KY', name: 'Cayman Islands' },
  { iso: 'KZ', name: 'Kazakhstan', googleId: 'kz' },
  { iso: 'LA', name: 'Laos', googleId: 'la' },
  { iso: 'LB', name: 'Lebanon', googleId: 'lb' },
  { iso: 'LC', name: 'St. Lucia' },
  { iso: 'LI', name: 'Liechtenstein' },
  { iso: 'LK', name: 'Sri Lanka', googleId: 'lk' },
  { iso: 'LR', name: 'Liberia' },
  { iso: 'LS', name: 'Lesotho' },
  { iso: 'LT', name: 'Lithuania' },
  { iso: 'LU', name: 'Luxembourg' },
  { iso: 'LV', name: 'Latvia' },
  { iso: 'LY', name: 'Libya' },
  { iso: 'MA', name: 'Kingdom of Morocco' },
  { iso: 'MC', name: 'Monaco' },
  { iso: 'MD', name: 'Moldova' },
  { iso: 'ME', name: 'Montenegro' },
  { iso: 'MF', name: 'St. Martin' },
  { iso: 'MG', name: 'Madagascar' },
  { iso: 'MH', name: 'Marshall Islands', googleId: 'mh' },
  { iso: 'MK', name: 'Macedonia' },
  { iso: 'ML', name: 'Republic of Mali' },
  { iso: 'MM', name: 'Myanmar', googleId: 'mm' },
  { iso: 'MN', name: 'Mongolia', googleId: 'mn' },
  { iso: 'MO', name: 'Macau SAR China', googleId: 'mo' },
  { iso: 'MP', name: 'Northern Mariana Islands', googleId: 'mp' },
  { iso: 'MQ', name: 'Martinique' },
  { iso: 'MR', name: 'Mauritania' },
  { iso: 'MS', name: 'Montserrat' },
  { iso: 'MT', name: 'Malta' },
  { iso: 'MU', name: 'Mauritius', googleId: 'mu' },
  { iso: 'MV', name: 'Maldives', googleId: 'mv' },
  { iso: 'MW', name: 'Malawi' },
  { iso: 'MX', name: 'Mexico' },
  { iso: 'MY', name: 'Malaysia' },
  { iso: 'MZ', name: 'Mozambique' },
  { iso: 'NA', name: 'Namibia' },
  { iso: 'NC', name: 'New Caledonia' },
  { iso: 'NE', name: 'Republic of the Niger' },
  { iso: 'NF', name: 'Norfolk Island', googleId: 'nf' },
  { iso: 'NG', name: 'Nigeria' },
  { iso: 'NI', name: 'Nicaragua' },
  { iso: 'NL', name: 'Netherlands' },
  { iso: 'NO', name: 'Norway' },
  { iso: 'NP', name: 'Nepal', googleId: 'np' },
  { iso: 'NR', name: 'Nauru', googleId: 'nr' },
  { iso: 'NU', name: 'Niue', googleId: 'nu' },
  { iso: 'NZ', name: 'New Zealand' },
  { iso: 'OM', name: 'Oman', googleId: 'om' },
  { iso: 'PA', name: 'Panama' },
  { iso: 'PE', name: 'Peru' },
  { iso: 'PF', name: 'French Polynesia', googleId: 'pf' },
  { iso: 'PG', name: 'Papua New Guinea', googleId: 'pg' },
  { iso: 'PH', name: 'Philippines' },
  { iso: 'PK', name: 'Pakistan', googleId: 'pk' },
  { iso: 'PL', name: 'Poland' },
  { iso: 'PM', name: 'St. Pierre & Miquelon' },
  { iso: 'PN', name: 'Pitcairn Islands', googleId: 'pn' },
  { iso: 'PR', name: 'Puerto Rico' },
  { iso: 'PS', name: 'Palestinian Territories', googleId: 'ps' },
  { iso: 'PT', name: 'Portugal' },
  { iso: 'PW', name: 'Palau', googleId: 'pw' },
  { iso: 'PY', name: 'Paraguay' },
  { iso: 'QA', name: 'Qatar', googleId: 'qa' },
  { iso: 'RE', name: 'Réunion' },
  { iso: 'RO', name: 'Romania' },
  { iso: 'RS', name: 'Serbia' },
  { iso: 'RU', name: 'Russia' },
  { iso: 'RW', name: 'Rwanda' },
  { iso: 'SA', name: 'Saudi Arabia', googleId: 'sa' },
  { iso: 'SB', name: 'Solomon Islands', googleId: 'sb' },
  { iso: 'SC', name: 'Seychelles' },
  { iso: 'SD', name: 'Sudan' },
  { iso: 'SE', name: 'Sweden' },
  { iso: 'SG', name: 'Singapore' },
  { iso: 'SH', name: 'St. Helena' },
  { iso: 'SI', name: 'Slovenia' },
  { iso: 'SJ', name: 'Svalbard & Jan Mayen' },
  { iso: 'SK', name: 'Slovakia' },
  { iso: 'SL', name: 'Sierra Leone' },
  { iso: 'SM', name: 'San Marino' },
  { iso: 'SN', name: 'Senegal' },
  { iso: 'SO', name: 'Somalia' },
  { iso: 'SR', name: 'Suriname' },
  { iso: 'SS', name: 'South Sudan' },
  { iso: 'ST', name: 'São Tomé & Príncipe' },
  { iso: 'SV', name: 'El Salvador' },
  { iso: 'SX', name: 'Sint Maarten' },
  { iso: 'SY', name: 'Syria', googleId: 'sy' },
  { iso: 'SZ', name: 'Swaziland' },
  { iso: 'TC', name: 'Turks & Caicos Islands' },
  { iso: 'TD', name: 'Chad' },
  { iso: 'TF', name: 'French Southern Territories', googleId: 'tf' },
  { iso: 'TG', name: 'Togolese Republic' },
  { iso: 'TH', name: 'Thailand' },
  { iso: 'TJ', name: 'Tajikistan', googleId: 'tj' },
  { iso: 'TK', name: 'Tokelau', googleId: 'tk' },
  { iso: 'TL', name: 'Timor-Leste', googleId: 'tl' },
  { iso: 'TM', name: 'Turkmenistan', googleId: 'tm' },
  { iso: 'TN', name: 'Tunisia' },
  { iso: 'TO', name: 'Tonga' },
  { iso: 'TR', name: 'Turkey' },
  { iso: 'TT', name: 'Trinidad & Tobago' },
  { iso: 'TV', name: 'Tuvalu', googleId: 'tv' },
  { iso: 'TW', name: 'Taiwan' },
  { iso: 'TZ', name: 'Tanzania' },
  { iso: 'UA', name: 'Ukraine' },
  { iso: 'UG', name: 'Uganda' },
  { iso: 'UM', name: 'U.S. Outlying Islands', googleId: 'um' },
  {
    iso: 'US',
    name: 'United States',
    googleId: 'usa',
    searchAliases: ['USA', 'U.S.', 'America'],
    subdivisionType: 'state',
    subdivisions: [
      { iso: 'US-AK', name: 'Alaska' },
      { iso: 'US-AL', name: 'Alabama' },
      { iso: 'US-AR', name: 'Arkansas' },
      { iso: 'US-AZ', name: 'Arizona' },
      { iso: 'US-CA', name: 'California' },
      { iso: 'US-CO', name: 'Colorado' },
      { iso: 'US-CT', name: 'Connecticut' },
      { iso: 'US-DC', name: 'District of Columbia' },
      { iso: 'US-DE', name: 'Delaware' },
      { iso: 'US-FL', name: 'Florida' },
      { iso: 'US-GA', name: 'Georgia' },
      { iso: 'US-HI', name: 'Hawaii' },
      { iso: 'US-IA', name: 'Iowa' },
      { iso: 'US-ID', name: 'Idaho' },
      { iso: 'US-IL', name: 'Illinois' },
      { iso: 'US-IN', name: 'Indiana' },
      { iso: 'US-KS', name: 'Kansas' },
      { iso: 'US-KY', name: 'Kentucky' },
      { iso: 'US-LA', name: 'Louisiana' },
      { iso: 'US-MA', name: 'Massachusetts' },
      { iso: 'US-MD', name: 'Maryland' },
      { iso: 'US-ME', name: 'Maine' },
      { iso: 'US-MI', name: 'Michigan' },
      { iso: 'US-MN', name: 'Minnesota' },
      { iso: 'US-MO', name: 'Missouri' },
      { iso: 'US-MS', name: 'Mississippi' },
      { iso: 'US-MT', name: 'Montana' },
      { iso: 'US-NC', name: 'North Carolina' },
      { iso: 'US-ND', name: 'North Dakota' },
      { iso: 'US-NE', name: 'Nebraska' },
      { iso: 'US-NH', name: 'New Hampshire' },
      { iso: 'US-NJ', name: 'New Jersey' },
      { iso: 'US-NM', name: 'New Mexico' },
      { iso: 'US-NV', name: 'Nevada' },
      { iso: 'US-NY', name: 'New York' },
      { iso: 'US-OH', name: 'Ohio' },
      { iso: 'US-OK', name: 'Oklahoma' },
      { iso: 'US-OR', name: 'Oregon' },
      { iso: 'US-PA', name: 'Pennsylvania' },
      { iso: 'US-RI', name: 'Rhode Island' },
      { iso: 'US-SC', name: 'South Carolina' },
      { iso: 'US-SD', name: 'South Dakota' },
      { iso: 'US-TN', name: 'Tennessee' },
      { iso: 'US-TX', name: 'Texas' },
      { iso: 'US-UT', name: 'Utah' },
      { iso: 'US-VA', name: 'Virginia' },
      { iso: 'US-VT', name: 'Vermont' },
      { iso: 'US-WA', name: 'Washington' },
      { iso: 'US-WI', name: 'Wisconsin' },
      { iso: 'US-WV', name: 'West Virginia' },
      { iso: 'US-WY', name: 'Wyoming' },
    ],
  },
  { iso: 'UY', name: 'Uruguay' },
  { iso: 'UZ', name: 'Uzbekistan', googleId: 'uz' },
  { iso: 'VA', name: 'Vatican City' },
  { iso: 'VC', name: 'St. Vincent & Grenadines' },
  { iso: 'VE', name: 'Venezuela' },
  { iso: 'VG', name: 'British Virgin Islands' },
  { iso: 'VI', name: 'U.S. Virgin Islands' },
  { iso: 'VN', name: 'Vietnam' },
  { iso: 'VU', name: 'Vanuatu' },
  { iso: 'WF', name: 'Wallis & Futuna' },
  { iso: 'WS', name: 'Samoa' },
  { iso: 'YE', name: 'Yemen', googleId: 'ye' },
  { iso: 'YT', name: 'Mayotte' },
  { iso: 'ZA', name: 'South Africa' },
  { iso: 'ZM', name: 'Zambia' },
  { iso: 'ZW', name: 'Zimbabwe ' },

  // Retired by ISO 3166-3. Present so codes stored before the change still resolve to a name.
  // Never selectable - use selectableCountries() for anything user-facing.
  { iso: 'AN', name: 'Netherlands Antilles', deprecated: true, supersededBy: ['CW', 'SX', 'BQ'] },
  { iso: 'BU', name: 'Burma', deprecated: true, supersededBy: ['MM'] },
  // ISO used CS twice: Czechoslovakia (1974-1993) and then Serbia and Montenegro (2003-2006).
  { iso: 'CS', name: 'Serbia and Montenegro', deprecated: true, supersededBy: ['RS', 'ME'] },
  { iso: 'CT', name: 'Canton and Enderbury Islands', deprecated: true, supersededBy: ['KI'] },
  { iso: 'DD', name: 'German Democratic Republic', deprecated: true, supersededBy: ['DE'] },
  { iso: 'DY', name: 'Dahomey', deprecated: true, supersededBy: ['BJ'] },
  { iso: 'FQ', name: 'French Southern and Antarctic Territories', deprecated: true, supersededBy: ['TF'] },
  { iso: 'FX', name: 'France, Metropolitan', deprecated: true, supersededBy: ['FR'] },
  { iso: 'HV', name: 'Upper Volta', deprecated: true, supersededBy: ['BF'] },
  { iso: 'JT', name: 'Johnston Island', deprecated: true, supersededBy: ['UM'] },
  { iso: 'MI', name: 'Midway Islands', deprecated: true, supersededBy: ['UM'] },
  { iso: 'NH', name: 'New Hebrides', deprecated: true, supersededBy: ['VU'] },
  { iso: 'NQ', name: 'Dronning Maud Land', deprecated: true, supersededBy: ['AQ'] },
  { iso: 'NT', name: 'Neutral Zone', deprecated: true, supersededBy: ['IQ', 'SA'] },
  { iso: 'PC', name: 'Pacific Islands (Trust Territory)', deprecated: true, supersededBy: ['FM', 'MH', 'MP', 'PW'] },
  { iso: 'PU', name: 'United States Miscellaneous Pacific Islands', deprecated: true, supersededBy: ['UM'] },
  { iso: 'PZ', name: 'Panama Canal Zone', deprecated: true, supersededBy: ['PA'] },
  { iso: 'RH', name: 'Southern Rhodesia', deprecated: true, supersededBy: ['ZW'] },
  { iso: 'SU', name: 'USSR', deprecated: true, supersededBy: ['AM', 'AZ', 'BY', 'EE', 'GE', 'KZ', 'KG', 'LV', 'LT', 'MD', 'RU', 'TJ', 'TM', 'UA', 'UZ'] },
  { iso: 'TP', name: 'East Timor', deprecated: true, supersededBy: ['TL'] },
  { iso: 'VD', name: 'Viet-Nam, Democratic Republic of', deprecated: true, supersededBy: ['VN'] },
  { iso: 'WK', name: 'Wake Island', deprecated: true, supersededBy: ['UM'] },
  { iso: 'YD', name: 'Yemen, Democratic', deprecated: true, supersededBy: ['YE'] },
  { iso: 'YU', name: 'Yugoslavia', deprecated: true, supersededBy: ['BA', 'HR', 'MK', 'ME', 'RS', 'SI'] },
  { iso: 'ZR', name: 'Zaire', deprecated: true, supersededBy: ['CD'] },
]

// Countries offerable in a picker. Retired codes stay in `countries` so already-stored values
// still resolve to a name, but they must never be assignable again.
export const selectableCountries = (): ICountry[] => countries.filter((country) => !country.deprecated)

// The countries most customers pick, in the order they should appear - not alphabetical, so the
// list reads as a priority order. Everything else follows alphabetically.
const PRIORITY_COUNTRIES = ['US', 'CA', 'GB', 'AU', 'DE', 'FR', 'IT', 'ES', 'NL', 'SE', 'NO', 'DK', 'FI']

/**
 * Selectable countries in the order every picker should show them. Kept separate from
 * selectableCountries(), which also feeds the AI-onboarding prompt and e2e fixtures where display
 * order is meaningless.
 */
export const countriesForPicker = (): ICountry[] => {
  const priority: ICountry[] = []
  const rest: ICountry[] = []

  for (const country of selectableCountries()) {
    ;(PRIORITY_COUNTRIES.includes(country.iso) ? priority : rest).push(country)
  }

  priority.sort((a, b) => PRIORITY_COUNTRIES.indexOf(a.iso) - PRIORITY_COUNTRIES.indexOf(b.iso))
  rest.sort((a, b) => a.name.localeCompare(b.name))

  return [...priority, ...rest]
}

// Spelled out rather than built from the type: translation keys may never be template strings.
const SUBDIVISION_TYPE_MESSAGE_IDS: Record<SubdivisionType, string> = {
  canton: 'locationPlace.term.canton',
  community: 'locationPlace.term.community',
  county: 'locationPlace.term.county',
  department: 'locationPlace.term.department',
  entity: 'locationPlace.term.entity',
  island: 'locationPlace.term.island',
  nation: 'locationPlace.term.nation',
  parish: 'locationPlace.term.parish',
  province: 'locationPlace.term.province',
  region: 'locationPlace.term.region',
  state: 'locationPlace.term.state',
}

const byLabel = (a: ISubdivisionOption, b: ISubdivisionOption): number => a.label.localeCompare(b.label)

export const findCountry = (countryCode: string): ICountry | undefined => countries.find((country) => country.iso === countryCode)

export const countryName = (countryCode: string): string | undefined => findCountry(countryCode)?.name

export const subdivisionOptions = (countryCode: string): ISubdivisionOption[] =>
  (findCountry(countryCode)?.subdivisions ?? []).map((subdivision) => ({ value: subdivision.iso, label: subdivision.name })).sort(byLabel)

export const hasSubdivisions = (countryCode: string): boolean => Boolean(findCountry(countryCode)?.subdivisions?.length)

/**
 * The subdivision part of an ISO 3166-2 code: 'DE-BY' -> 'BY'. Already-stored bare codes have no
 * dash and pass through unchanged.
 *
 * Two consumers depend on this, for unrelated reasons: date-holidays keys subdivisions by the bare
 * suffix and falls back to national holidays on an unknown key rather than erroring, and
 * Stripe's `address.state` has always held the bare code. Changing it touches holidays AND billing.
 */
export const bareSubdivisionCode = (subdivisionCode?: string | null): string | undefined => {
  if (!subdivisionCode) return undefined
  const dash = subdivisionCode.indexOf('-')
  return dash === -1 ? subdivisionCode : subdivisionCode.slice(dash + 1)
}

// Accepts a full ISO code ('DE-BY') or a legacy bare one ('by'), which locations saved before the
// switch still hold. The country has to be supplied for the bare form - it carries no prefix.
export const findSubdivision = (subdivisionCode: string, countryCode?: string): ISubdivision | undefined => {
  const country = findCountry(countryCode ?? subdivisionCode.split('-')[0])
  const bare = bareSubdivisionCode(subdivisionCode)?.toUpperCase()
  return country?.subdivisions?.find((subdivision) => bareSubdivisionCode(subdivision.iso) === bare)
}

export const subdivisionTypeOf = (countryCode: string): SubdivisionType => findCountry(countryCode)?.subdivisionType ?? 'region'

export const subdivisionTermKey = (countryCode: string): string => SUBDIVISION_TYPE_MESSAGE_IDS[subdivisionTypeOf(countryCode)]

export const subdivisionName = (place: IPlace): string | undefined => (place.subdivisionCode ? findSubdivision(place.subdivisionCode, place.countryCode)?.name : undefined)

export const placeLabel = (place: IPlace): string => [countryName(place.countryCode), subdivisionName(place)].filter(Boolean).join(' · ')

// Mirrors the API's duplicate check in backend/src/common/name/name-validator.ts, which compares
// with lodash `lowerCase`. That collapses punctuation and separators as well as case, so
// 'Spain · Catalunya', 'Spain Catalunya' and 'spain-catalunya' are all the same name. Comparing
// with a plain toLowerCase() here would let the UI offer a name the API then rejects.
const isSameLocationName = (a: string, b: string): boolean => lowerCase(a) === lowerCase(b)

export const isLocationNameTaken = (name: string, existingNames: string[]): boolean => Boolean(name) && existingNames.some((existing) => isSameLocationName(existing, name))

// 'Germany' -> 'Germany 2' when the name is already taken.
export const uniqueLocationName = (name: string, existingNames: string[]): string => {
  if (!name || !isLocationNameTaken(name, existingNames)) return name

  let suffix = 2
  while (isLocationNameTaken(`${name} ${suffix}`, existingNames)) {
    suffix += 1
  }
  return `${name} ${suffix}`
}

// Which half of the place is still unset: the country, or a subdivision the country expects.
export const missingKind = (place: IPlace): 'country' | 'subdivision' | null => {
  if (!place.countryCode) return 'country'
  return hasSubdivisions(place.countryCode) && !place.subdivisionCode ? 'subdivision' : null
}

export default countries
